import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Building2, User, Mail, Lock, Truck, CheckCircle2, Clock, AlertCircle, ArrowRight, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE as API } from '../config';

export default function JoinPage() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState('signup'); // 'signup' or 'link'

  // Signup form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');

  // Link form
  const [linkEmail, setLinkEmail] = useState('');
  const [linkPassword, setLinkPassword] = useState('');

  useEffect(() => {
    axios.get(`${API}/join/${code}`)
      .then(res => {
        setInfo(res.data);
        if (res.data.email) setEmail(res.data.email);
        if (res.data.company_name) setCompanyName(res.data.company_name);
      })
      .catch(err => setError(err.response?.data?.detail || 'Invalid or expired invite link'))
      .finally(() => setLoading(false));
  }, [code]);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/join/${code}`, { name, email, password, company_name: companyName });
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLink = async (e) => {
    e.preventDefault();
    if (!linkEmail || !linkPassword) { toast.error('Email and password are required'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/join/${code}/link`, { email: linkEmail, password: linkPassword });
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center p-4">
        <div className="text-gray-500">Loading invite...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="pt-6 pb-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-gray-900 mb-1">Invalid Invite</h2>
            <p className="text-sm text-gray-500 mb-4">{error}</p>
            <Link to="/login">
              <Button variant="outline">Go to Sign In</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (result) {
    const isPending = result.status === 'pending_approval';
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="pt-6 pb-6 text-center">
            {isPending ? (
              <>
                <Clock className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                <h2 className="text-lg font-bold text-gray-900 mb-1">Request Sent</h2>
                <p className="text-sm text-gray-500 mb-4">{result.message}</p>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-12 h-12 text-teal-500 mx-auto mb-3" />
                <h2 className="text-lg font-bold text-gray-900 mb-1">You're In!</h2>
                <p className="text-sm text-gray-500 mb-4">{result.message}</p>
              </>
            )}
            <Link to="/login">
              <Button className={isPending ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-500 hover:bg-teal-600'} data-testid="join-go-signin">
                {isPending ? 'Go to Sign In' : 'Sign In to Get Started'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const brandColor = info?.branding_color || '#2563eb';

  return (
    <div className="min-h-screen bg-gray-200 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-5">
          <img src="/logo.png" alt="Logo" className="w-10 h-10 mx-auto mb-2" />
          <p className="text-xs text-gray-400">CMS By All About Claims</p>
        </div>

        <Card className="shadow-lg overflow-hidden" data-testid="join-card">
          {/* Org header */}
          <div className="px-6 pt-5 pb-4 border-b border-gray-100" style={{ borderTopColor: brandColor, borderTopWidth: 4 }}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: brandColor + '18' }}>
                <Building2 className="w-5 h-5" style={{ color: brandColor }} />
              </div>
              <div>
                <h2 className="font-bold text-gray-900" data-testid="join-org-name">{info?.provider_name || 'Claims Provider'}</h2>
                <p className="text-xs text-gray-500">Join this provider's network</p>
              </div>
            </div>
            {info?.type === 'email' && (
              <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700">
                <Mail className="w-3.5 h-3.5 inline mr-1.5" />
                You've been invited via email
                {info.company_name && <span> for <strong>{info.company_name}</strong></span>}
              </div>
            )}
            {info?.require_approval && info?.type !== 'email' && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
                <Clock className="w-3.5 h-3.5 inline mr-1.5" />
                This organization requires approval to join
              </div>
            )}
          </div>

          {/* Tab switcher */}
          <div className="flex border-b border-gray-100">
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium transition-colors ${mode === 'signup' ? 'text-gray-900 border-b-2' : 'text-gray-400 hover:text-gray-600'}`}
              style={mode === 'signup' ? { borderBottomColor: brandColor } : {}}
              data-testid="join-tab-signup"
            >
              <User className="w-4 h-4" /> Create Account
            </button>
            <button
              onClick={() => setMode('link')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium transition-colors ${mode === 'link' ? 'text-gray-900 border-b-2' : 'text-gray-400 hover:text-gray-600'}`}
              style={mode === 'link' ? { borderBottomColor: brandColor } : {}}
              data-testid="join-tab-link"
            >
              <LogIn className="w-4 h-4" /> Link Existing Account
            </button>
          </div>

          <CardContent className="pt-5 pb-6">
            {mode === 'signup' ? (
              <>
                <p className="text-xs text-gray-500 mb-3">Create a new account and join this organization. Your company will be set up as a managed tenant.</p>
                <form onSubmit={handleSignup} className="space-y-3" data-testid="join-signup-form">
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)} className="pl-9 h-10" required data-testid="join-name" />
                  </div>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9 h-10" required readOnly={!!info?.email} data-testid="join-email" />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input type="password" placeholder="Create Password (min 6)" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9 h-10" required data-testid="join-password" />
                  </div>
                  <div className="relative">
                    <Truck className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input placeholder="Your Company Name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="pl-9 h-10" readOnly={!!info?.company_name} data-testid="join-company" />
                  </div>
                  <Button type="submit" className="w-full h-11 font-semibold text-white" style={{ backgroundColor: brandColor }} disabled={submitting} data-testid="join-submit">
                    {submitting ? 'Creating Account...' : info?.require_approval && info?.type !== 'email' ? 'Request to Join' : 'Create Account & Join'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-3">Already have a CMS account? Sign in below to link your existing organization to this provider. Your existing data will transfer automatically.</p>
                <form onSubmit={handleLink} className="space-y-3" data-testid="join-link-form">
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input type="email" placeholder="Your existing email" value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} className="pl-9 h-10" required data-testid="join-link-email" />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input type="password" placeholder="Password" value={linkPassword} onChange={(e) => setLinkPassword(e.target.value)} className="pl-9 h-10" required data-testid="join-link-password" />
                  </div>
                  <Button type="submit" className="w-full h-11 font-semibold text-white" style={{ backgroundColor: brandColor }} disabled={submitting} data-testid="join-link-submit">
                    {submitting ? 'Linking...' : info?.require_approval && info?.type !== 'email' ? 'Request to Link' : 'Link Account & Join'}
                    <LogIn className="w-4 h-4 ml-2" />
                  </Button>
                </form>
                <p className="text-[11px] text-gray-400 mt-3 text-center">
                  Your existing data (companies, claims) will become visible to this provider. Your billing will transfer to the provider's plan.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
